/**
 * Voice Output (TTS) utilities using browser SpeechSynthesis API
 * Supports Hindi and English voices
 */

/**
 * Speak text aloud using browser TTS
 * @param {string} text - Text to speak
 * @param {string} lang - BCP-47 language tag (default: 'hi-IN')
 * @returns {SpeechSynthesisUtterance|null} The utterance object (for onend listener)
 */
export const speakText = (text, lang = 'hi-IN') => {
  if (!('speechSynthesis' in window)) {
    console.warn('[TTS] SpeechSynthesis not supported in this browser');
    return null;
  }

  // Cancel any ongoing speech
  window.speechSynthesis.cancel();

  // Clean text — remove markdown, emojis, special markers
  const cleanText = text
    .replace(/[#*_~`]/g, '')
    .replace(/\[.*?\]/g, '')
    .replace(/[📦🏗️🔴🟡✅⚠️📋🔧📊💰🚧📐🧪📸🔍🔨📅📈📉🛡️🎤✓✗🙏👋👤🌍✨🛤️📚🔗]/gu, '')
    .replace(/\n{2,}/g, '. ')
    .replace(/\n/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();

  if (!cleanText) return null;

  const utterance = new SpeechSynthesisUtterance(cleanText);

  // Try to find a Hindi or Indian English voice
  const voices = window.speechSynthesis.getVoices();
  const hindiVoice = voices.find(v => v.lang.startsWith('hi'))
    || voices.find(v => v.lang.includes('IN'))
    || voices.find(v => v.lang.startsWith('en'));

  if (hindiVoice) {
    utterance.voice = hindiVoice;
    console.log('[TTS] Using voice:', hindiVoice.name, hindiVoice.lang);
  }

  utterance.lang = lang;
  utterance.rate = 1.0;
  utterance.pitch = 1.0;
  utterance.volume = 1.0;

  window.speechSynthesis.speak(utterance);
  return utterance;
};

/**
 * Stop any ongoing speech
 */
export const stopSpeaking = () => {
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
};

/**
 * Check if TTS is supported in this browser
 */
export const isTTSSupported = () => {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
};
